import AriaModal from 'react-aria-modal';
import styled, { keyframes } from 'styled-components';
import { useCookieConsentClosed } from '../../../cookie-consent';
import { CloseButton } from '../CloseButton';
import { columnWidth } from '../../mixins';
import {
  breakpointMedium,
  colorBackgroundDefault,
  colorBorderDecorative,
} from '../../design-tokens';
import {
  borderRadius,
  spacing2,
  spacing4,
  spacing6,
  spacing8,
  spacing12,
} from 'lib/mortar';

export type ModalSizes = 'small' | 'medium' | 'large';

const modalDesktopColumn = {
  small: 4,
  medium: 6,
  large: 8,
};

const modalTabletColumn = {
  small: 6,
  medium: 8,
  large: 10,
};

const paddingTop = (size: ModalSizes) =>
  size === 'small' ? spacing8 : spacing12;

const animationSpeed = '250ms';

const slideUpKeyFrame = keyframes`
  from {
    opacity: 0;
    transform: translateY(50%);
  }
  to {
    opacity: 1;
    transform:  translateY(0);
  }
`;

const underlayStyle = `
  @keyframes fadeIn {
    from {
      background-color: rgba(0, 0, 0, 0);
      backdrop-filter: blur(0);
      -webkit-backdrop-filter: 'blur(0)',
    }
    to {
      background-color: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(5px);
      -webkit-backdrop-filter: 'blur(5px)',
    }
  }
  .modal-underlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1050;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    background-color: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    animation: fadeIn ${animationSpeed} ease-in-out;
  }
  @media (min-width: ${breakpointMedium.start}) {
    .modal-underlay {
      align-items: center;
    }
  }
`;

const ModalCloseButton = styled(CloseButton)<{
  modalSize: ModalSizes;
}>`
  position: absolute;
  z-index: 1000;
  top: ${spacing2};
  right: ${spacing2};
  ${props =>
    props.modalSize === 'small' &&
    `
      @media (min-width: ${breakpointMedium.start}) {
        top: 0;
        right: 0;
      }
  `}
`;

const Wrapper = styled.div<
  StyledDiv & {
    size: ModalSizes;
    fullScreenOnMobile: boolean;
    ignorePadding: boolean;
  }
>`
  ${props =>
    props.fullScreenOnMobile
      ? `
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  `
      : 'position: relative;'}
  ${props =>
    columnWidth(
      4,
      modalTabletColumn[props.size],
      modalDesktopColumn[props.size],
    )}
  padding-top: ${spacing12};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: 1px solid ${colorBorderDecorative};
  border-top-left-radius: ${props => (props.fullScreenOnMobile ? 0 : spacing2)};
  border-top-right-radius: ${props =>
    props.fullScreenOnMobile ? 0 : spacing2};
  box-shadow: 0px 0px 25px rgba(72, 102, 131, 0.25);
  background-color: ${colorBackgroundDefault};
  animation: ${slideUpKeyFrame} ${animationSpeed} ease-in-out forwards;

  @media (min-width: ${breakpointMedium.start}) {
    position: relative;
    height: initial;
    max-height: 75vh;
    padding-top: ${props => (props.ignorePadding ? 0 : paddingTop(props.size))};
    border-radius: ${borderRadius(2)};
  }
`;

const calcScrollableWrapperPadding = (size: ModalSizes) =>
  size === 'small'
    ? `0 ${spacing8} ${spacing6}`
    : `0 ${spacing12} ${spacing12}`;

export const ScrollableWrapper = styled.div<
  StyledDiv & {
    size: ModalSizes;
    minHeight?: string;
    fullScreenOnMobile: boolean;
    ignorePadding: boolean;
    scrollable: boolean;
  }
>`
  ${({ scrollable }) => scrollable && `overflow: auto`};
  ${({ minHeight }) => minHeight && `min-height: ${minHeight}`};
  max-height: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: ${props =>
    props.fullScreenOnMobile || props.ignorePadding
      ? 0
      : `0 ${spacing4} ${spacing6}`};
  @media (min-width: ${breakpointMedium.start}) {
    height: 100vh;
    max-height: ${props => `calc(75vh - ${paddingTop(props.size)})`};
    padding: ${props =>
      props.ignorePadding ? 0 : calcScrollableWrapperPadding(props.size)};
  }
`;

export const Modal = ({
  ariaLabel,
  ariaLabelledBy,
  onDismiss,
  minHeight,
  size = 'small',
  fullScreenOnMobile = false,
  applicationNode = '__next',
  initialFocus = '.modal-close-btn',
  underlayClickExits = false,
  ignorePadding = false,
  scrollable = true,
  children,
}: ModalProps) => {
  const cookieConsentClosed = useCookieConsentClosed();

  const getApplicationNode = (): Node | Element => {
    return document.getElementById(applicationNode) as Node;
  };

  const ariaProps = ariaLabel
    ? {
        titleText: ariaLabel as string,
      }
    : {
        titleId: ariaLabelledBy as string,
      };

  return (
    <>
      <style>{underlayStyle}</style>
      <AriaModal
        {...ariaProps}
        onExit={onDismiss}
        initialFocus={initialFocus}
        getApplicationNode={getApplicationNode}
        underlayClickExits={underlayClickExits}
        includeDefaultStyles={false}
        underlayClass="modal-underlay"
        focusTrapPaused={!cookieConsentClosed}
      >
        <Wrapper
          size={size}
          fullScreenOnMobile={fullScreenOnMobile}
          ignorePadding={ignorePadding}
          data-testid="modal"
        >
          <ModalCloseButton
            className="modal-close-btn"
            onClick={onDismiss}
            modalSize={size}
          />
          <ScrollableWrapper
            minHeight={minHeight}
            size={size}
            fullScreenOnMobile={fullScreenOnMobile}
            ignorePadding={ignorePadding}
            scrollable={scrollable}
          >
            {children}
          </ScrollableWrapper>
        </Wrapper>
      </AriaModal>
    </>
  );
};

type RequiredAriaProps =
  | {
      ariaLabel: string;
    }
  | {
      ariaLabelledBy: string;
    };

type ModalProps = {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  onDismiss: () => void;
  size?: ModalSizes;
  minHeight: string;
  fullScreenOnMobile?: boolean;
  applicationNode?: string;
  initialFocus?: string;
  underlayClickExits?: boolean;
  ignorePadding?: boolean;
  scrollable?: boolean;
  children: React.ReactNode;
} & RequiredAriaProps;
